import { getAuthToken } from '../../../lib/api';
import { API_ORIGIN } from '../../../lib/apiBase';

// Shape of GET /admin-dashboard/summary (api/functions/admin-dashboard.js).
// A section that failed server-side comes back as { error } instead of its data.

export type Failed = { error: string };
export const failed = (x: unknown): x is Failed => !!x && typeof x === 'object' && 'error' in (x as object);

export type ChatSection = {
  online: boolean;
  now: { waiting: number; active: number; withTeam: number };
  days: { date: string; bot: number; team: number }[];
  prevDays: { date: string; total: number }[];
  totals: { chats: number; team: number; prevChats: number; prevTeam: number; visitorMessages: number; aiMessages: number; adminMessages: number };
  latest: ChatRow[];
};

export type ChatRow = {
  id: string;
  visitor: string | null;
  page: string | null;
  status: string;
  createdAt: string;
  lastMessageAt: string | null;
  messages: number;
  handedOver: boolean;
  unread: number;
  lastMessage: string | null;
  lastSender: string | null;
};

export type Traffic = { sessions: number; visitors: number; pageviews: number };
export type City = { city: string; country: string; sessions: number; lat?: number | null; lng?: number | null };
/** Per-session sums; averages are derived on the client so they stay consistent with the totals. */
export type Engagement = { sessions: number; duration: number; bounces: number; pageviews: number };
export type SiteHealth = {
  samples: number;
  lcp: number | null;
  inp: number | null;
  cls: number | null;
  fcp: number | null;
  rage: number;
  dead: number;
  errors: number;
  pages: { path: string; rage: number; dead: number; errors: number }[];
};
export type Visitors = {
  connected: true;
  /** First day PostHog recorded the site; earlier days have no data. */
  since: string;
  days: ({ date: string } & Traffic)[];
  prevDays: ({ date: string } & Traffic)[];
  totals: Traffic;
  prevTotals: Traffic;
  topPages: { path: string; views: number }[];
  topSources: { source: string; sessions: number }[];
  engagement: { cur: Engagement; prev: Engagement };
  /** PostHog channel types, plus "Internal" for sessions that started from one of the site's own pages. */
  channels: { channel: string; sessions: number; bounces: number; duration: number }[];
  landing: { path: string; sessions: number; bounces: number }[];
  devices: { device: string; sessions: number }[];
  browsers?: { browser: string; sessions: number }[];
  /** Top 40 towns. `city` is empty when GeoIP only knew the country (lat/lng is then the country's centre). */
  cities: City[];
  countries?: { name: string; code: string; sessions: number }[];
  heat: { dow: number; hour: number; sessions: number; pageviews: number }[];
  health: SiteHealth;
  live: { visitors5m: number; perMinute: number[] };
};
export type VisitorsSection = { connected: false } | Visitors;

/** The visitors section when PostHog answered, else null. */
export const visitorsOf = (d: { visitors: VisitorsSection | Failed } | null | undefined): Visitors | null =>
  d && !failed(d.visitors) && d.visitors.connected ? d.visitors : null;

export type SyncRun = {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'failed' | 'dry_run';
  sourceFile: string | null;
  rowsStaged: number | null;
  upserted: number | null;
  retired: number | null;
  stylesWritten: number | null;
  orphansRemoved: number | null;
  error: string | null;
};

export type SyncSection = {
  runs: SyncRun[];
  catalogue: { liveStyles: number; liveSkus: number; discontinuedSkus: number; lastUpdated: string | null };
};

export type Summary = {
  success: true;
  generatedAt: string;
  range: { from: string; to: string; days: number; prevFrom: string; prevTo: string };
  chat: ChatSection | Failed;
  visitors: VisitorsSection | Failed;
  sync: SyncSection | Failed;
  health: { chat: { ok: boolean; ms: number } } | Failed;
  counts: { draftPosts: number; publishedPosts: number; caseStudies: number; draftCaseStudies?: number; liveStyles: number; adminUsers: number; openChats: number } | Failed;
};

export type Loaded = Summary;

export async function fetchSummary(from: string, to: string, signal?: AbortSignal): Promise<Loaded> {
  const res = await fetch(`${API_ORIGIN}/.netlify/functions/admin-dashboard/summary?from=${from}&to=${to}`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    signal,
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || `Dashboard failed (${res.status})`);
  return json as Summary;
}

// ---- dates (all ranges are Europe/London calendar days, as the API buckets them)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const DAY_MS = 864e5;

/** 'YYYY-MM-DD' → UTC midnight ms. */
export const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
export const isoDay = (t: number) => new Date(t).toISOString().slice(0, 10);
export const londonToday = () => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const get = (k: string) => parts.find((p) => p.type === k)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};
export const dayLabel = (s: string | number, withYear = false) => {
  const d = new Date(typeof s === 'string' ? parseDay(s) : s);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ''}`;
};
export const rangeLabel = (from: string, to: string) => {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${dayLabel(from, !sameYear)} – ${dayLabel(to, true)}`;
};

export const int = (n: number) => Math.round(n).toLocaleString('en-GB');
export const pctChange = (now: number, prev: number) => (prev > 0 ? ((now - prev) / prev) * 100 : null);
/** 127 → "2m 07s", 42 → "42s". */
export const mins = (sec: number) => {
  const s = Math.round(sec);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
export const plural = (n: number, one: string, many = `${one}s`) => `${int(n)} ${n === 1 ? one : many}`;

/** "4 min ago", "3 h ago", "2 days ago". */
export const ago = (iso: string | null | undefined, now = Date.now()) => {
  if (!iso) return '—';
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  const d = Math.round(s / 86400);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};
